import React from 'react'
import { useRouter } from 'next/router'

import { useToast } from '@wartek-id/toast'

import {
  useQueryAPIGetKSSelectionCandidate,
  useMutateAPIUploadSubmission,
  useQueryAPIGetKSInvitationDocuments,
  useMutateAPIDeleteKSInvitationDocuments,
} from 'api/bcks/useAPIBCKSInvitation'
import { SELECTION_STATES_FINAL_PAGE } from '../constants'
import AndroidHandler from 'utils/androidHandler'

export function useBCKSInvitationCollection() {
  const { data: selectionState } = useQueryAPIGetKSSelectionCandidate()
  const { data: documentState, refetch } = useQueryAPIGetKSInvitationDocuments(
    selectionState?.selectionId || ''
  )
  const submissionUploadMutation = useMutateAPIUploadSubmission(
    selectionState?.selectionId || ''
  )
  const deleteDocumentMutation = useMutateAPIDeleteKSInvitationDocuments()

  const router = useRouter()
  const androidHandler = AndroidHandler()

  const {
    docName = '' as String,
    docTitle = '' as string,
    docUrl = '' as string,
  } = router.query
  const isReviewMode = docName && docTitle && docUrl ? true : false

  const [docList, setDocList] = React.useState({
    pakta_integritas: {
      name: '',
      url: '',
      fileData: '',
    },
    kinerja: {
      name: '',
      url: '',
      fileData: '',
    },
    police: {
      name: '',
      url: '',
      fileData: '',
    },
    managerial: {
      name: '',
      url: '',
      fileData: '',
    },
    discipline: {
      name: '',
      url: '',
      fileData: '',
    },
  })
  const [currentDoc, setCurrentDoc] = React.useState({
    name: '',
    title: '',
  })
  const [currentUploadingDoc, setCurrentUploadingDoc] = React.useState({
    name: '',
    title: '',
  })
  const toast = useToast()

  if (
    SELECTION_STATES_FINAL_PAGE.includes(
      selectionState?.selectionCandidateState
    )
  ) {
    router.replace('/bcks/invitation/final')
  }

  const getDocUploadTitle = (docName: string) => {
    const docNameToTitleMap = {
      pakta_integritas: 'Pakta Integritas',
      kinerja: 'Hasil Penilaian Kinerja',
      police: 'Surat Keterangan Catatan Kepolisian',
      managerial: 'Surat Keterangan Pengalaman Manajerial',
      discipline: 'Surat Keterangan Bebas Hukuman Disiplin',
    }

    return docNameToTitleMap[docName] || 'Upload Dokumen'
  }

  const getDocNameToEnumPayloadDoc = (docName: string, reverse?: boolean) => {
    const docNameToPayloadDocMap = {
      pakta_integritas: 'PAKTA_INTEGRITAS',
      kinerja: 'HASIL_PENILAIAN_KINERJA',
      police: 'SKCK',
      managerial: 'SK_MANAJERIAL',
      discipline: 'SK_BEBAS_HUKUMAN',
    }

    const docNameToPayloadDocMapReverse = {
      pakta_integritas: 'pakta_integritas',
      HASIL_PENILAIAN_KINERJA: 'kinerja',
      SKCK: 'police',
      SK_MANAJERIAL: 'managerial',
      SK_BEBAS_HUKUMAN: 'discipline',
    }

    if (reverse) {
      return docNameToPayloadDocMapReverse[docName] || 'kinerja'
    }

    return docNameToPayloadDocMap[docName] || 'HASIL_PENILAIAN_KINERJA'
  }

  const handleCopyUrlClick = (urlToClipboard: string) => {
    if (androidHandler?.enabled) {
      androidHandler?.copyText(urlToClipboard)
      toast({
        message: 'Tautan berhasil disalin',
      })
      return
    }

    if (!navigator?.clipboard) {
      toast({
        message: 'Tautan gagal disalin',
      })
    }
    navigator.clipboard.writeText(urlToClipboard).then(
      function () {
        toast({
          message: 'Tautan berhasil disalin',
        })
      },
      function (err) {
        toast({
          message: `Tautan gagal disalin: ${err}`,
        })
      }
    )
  }

  const handleDocUpload = ({ docName }) => {
    const pageTitle = getDocUploadTitle(docName)
    setCurrentDoc({ name: docName, title: pageTitle })
  }

  const handleMutateDocList = (docName, docData) => {
    const { fileData } = docData

    const payload: FormData = new FormData()
    payload.append('selectionId', selectionState?.selectionId)
    payload.append('fileType', getDocNameToEnumPayloadDoc(docName))
    payload.append('file', fileData)

    setCurrentUploadingDoc({ name: docName, title: getDocUploadTitle(docName) })
    submissionUploadMutation.mutate(payload, {
      onSuccess: () => {
        toast({
          message: `Berhasil mengunggah dokumen ${getDocUploadTitle(docName)}!`,
        })
        refetch()
        if (isReviewMode) {
          handleNextStep()
        }
      },
      onError: (res) => {
        toast({
          message: `Gagal mengunggah dokumen: ${
            res.response.data.message || res.message
          }`,
        })
        refetch()
        if (isReviewMode) {
          handleNextStep()
        }
      },
      onSettled() {
        setCurrentUploadingDoc({ name: '', title: '' })
      },
    })
  }

  const handleRemoveTempDoc = (docName) => {
    const fileType = getDocNameToEnumPayloadDoc(docName)

    deleteDocumentMutation.mutate(
      { selectionId: selectionState?.selectionId, fileType },
      {
        onSuccess: () => {
          toast({
            message: `Berhasil menghapus dokumen ${getDocUploadTitle(
              docName
            )}!`,
          })
          refetch()
        },
        onError: () => {
          toast({
            message: `Gagal Menghapus Dokumen! Silahkan coba lagi`,
          })
        },
      }
    )
  }

  const isDocValid = React.useMemo(() => {
    return Object.values(docList).every((doc) => doc.url)
  }, [docList])

  const isDocDraftValid = React.useMemo(() => {
    return Object.values(docList).some((doc) => doc.url)
  }, [docList])

  const handleSaveDraft = () => {
    toast({
      message: `Draf telah tersimpan`,
    })

    router.replace('/bcks/invitation/precollection')
  }

  const handleNextStep = () => {
    router.replace('/bcks/invitation/review')
  }

  const handleAssignDocList = () => {
    if (selectionState?.selectionId && documentState) {
      setDocList({
        pakta_integritas: {
          name: (documentState.paktaIntegritasUrl && 'Pakta Integritas') || '',
          url: documentState.paktaIntegritasUrl,
          fileData: '',
        },
        kinerja: {
          name:
            (documentState.hasilPenilaianKinerjaUrl &&
              'Hasil Penilaian Kinerja') ||
            '',
          url: documentState.hasilPenilaianKinerjaUrl,
          fileData: '',
        },
        police: {
          name: (documentState.catatanKepolisianUrl && 'SKCK') || '',
          url: documentState.catatanKepolisianUrl,
          fileData: '',
        },
        managerial: {
          name:
            (documentState.pengalamanManajerialUrl && 'SK Manajerial') || '',
          url: documentState.pengalamanManajerialUrl,
          fileData: '',
        },
        discipline: {
          name: (documentState.bebasHukumanUrl && 'SK Bebas Hukuman') || '',
          url: documentState.bebasHukumanUrl,
          fileData: '',
        },
      })
    }
  }

  React.useEffect(() => {
    if (isReviewMode) {
      handleDocUpload({
        docName: docName as string,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewMode])

  React.useEffect(() => {
    handleAssignDocList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionState?.selectionId, documentState])

  return {
    handleCopyUrlClick,
    isDocValid,
    handleDocUpload,
    isUploading: submissionUploadMutation.isLoading,
    currentDoc,
    currentUploadingDoc,
    docList,
    setDocList,
    setCurrentDoc,
    handleMutateDocList,
    handleRemoveTempDoc,
    handleSaveDraft,
    handleNextStep,
    isReviewMode,
    isDocDraftValid,
  }
}
